import { IStuCover, IStuPaymentSummary, IStuSelection } from '@/types/stu';

/** An add-on's price as a change to the premium: "+ ₹1,780", or "−₹520" for a discount. */
export const formatAddonPrice = (cent: number) =>
  cent < 0 ? `−${formatRupee(-cent)}` : `+ ${formatRupee(cent)}`;

export const formatRupee = (cent: number) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cent / 100);

export const formatSumInsured = (rupees: number) =>
  rupees >= 10000000
    ? `₹${Number((rupees / 10000000).toFixed(2))} Crore`
    : `₹${Number((rupees / 100000).toFixed(2))} Lakh`;

export const formatTenure = (years: number) =>
  `${years} ${years > 1 ? 'years' : 'year'}`;

export const formatCover = (cover: {
  sumInsuredCent: number | null;
  sumInsuredUnlimited: boolean;
}) => {
  if (cover.sumInsuredUnlimited) return 'Unlimited';
  if (cover.sumInsuredCent === null) return '—';
  return formatSumInsured(cover.sumInsuredCent / 100);
};

/** The add-on master's name, shown as the server sends it. */
export const coverLabel = (cover: Pick<IStuCover, 'name'>) => cover.name;

/** A per-member add-on's chip: "Self · 34 yrs", with the age from the plan page's members. */
export const coverMemberLabel = (
  relation: string,
  members: Array<{ relation: string; age: number }>,
) => {
  const member = members.find(
    (item) => item.relation.toLowerCase() === relation.toLowerCase(),
  );
  const name = relation.charAt(0).toUpperCase() + relation.slice(1);
  return member ? `${name} · ${member.age} yrs` : name;
};

export const coverScopeNote = (cover: IStuCover) => {
  if (cover.coverScope !== 'member' || cover.eligibleMembers.length === 0) {
    return undefined;
  }

  const members = cover.eligibleMembers
    .map((relation) => relation.charAt(0).toUpperCase() + relation.slice(1))
    .join(', ');

  return `Applies to ${members}`;
};

/** The other add-ons sharing this one's group; only one add-on of a group can be added. */
export const addonGroupPartners = (covers: IStuCover[], cover: IStuCover) =>
  cover.groupCode
    ? covers.filter(
        (item) => item.groupCode === cover.groupCode && item.coverCode !== cover.coverCode,
      )
    : [];

export type TAddonTile =
  | { kind: 'single'; cover: IStuCover }
  | { kind: 'group'; groupCode: string; covers: IStuCover[] };

/**
 * The add-ons card in order: each lone add-on is its own tile, then each set of add-ons
 * sharing a group as one "pick 1 of N" tile, groups in the order their first add-on came.
 */
export const addonTiles = (covers: IStuCover[]): TAddonTile[] => {
  const tiles = covers.flatMap((cover): TAddonTile[] => {
    const partners = addonGroupPartners(covers, cover);
    if (!cover.groupCode || partners.length === 0) return [{ kind: 'single', cover }];
    const isFirstOfGroup = covers.find((item) => item.groupCode === cover.groupCode) === cover;
    return isFirstOfGroup
      ? [{ kind: 'group', groupCode: cover.groupCode, covers: [cover, ...partners] }]
      : [];
  });
  return [
    ...tiles.filter((tile) => tile.kind === 'single'),
    ...tiles.filter((tile) => tile.kind === 'group'),
  ];
};

/**
 * An added add-on at ₹0: the insurer charges it inside the base premium, so it isn't free
 * (guide 2026-10-16 §3).
 */
export const STU_ADDON_INCLUDED_LABEL = 'Included in premium';

/** An add-on choice as shown: the option with its unit ("20 Percent"), or its own cover. */
export const formatAddonChoice = ({
  option,
  optionLabel,
  sumInsuredCent,
}: {
  option?: string | null;
  optionLabel?: string | null;
  sumInsuredCent?: number | null;
}) =>
  [
    option ? [option, optionLabel].filter(Boolean).join(' ') : null,
    typeof sumInsuredCent === 'number' ? formatRupee(sumInsuredCent) : null,
  ]
    .filter(Boolean)
    .join(', ');

export const titleCase = (value: string) =>
  value
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export interface IStuPremiumLine {
  key: string;
  label: string;
  amountCent: number | null;
  /** Plan lines (base, built-in covers, discount) list before the add-ons the customer chose. */
  group: 'plan' | 'addon';
}

/** What the premium bar shows on every step: the total and how it adds up. */
export interface IStuPremium {
  totalCent: number | null;
  tenureYears: number;
  /** Empty when the server sends no split; then only the total shows. */
  lines: IStuPremiumLine[];
}

const memberNames = (members: { relation: string }[]) =>
  members.map((member) => titleCase(member.relation)).join(', ');

/** "₹5 Lakh" from paise; null when no deductible is reported. */
export const formatDeductibleCent = (cent: number | null | undefined) =>
  typeof cent === 'number' ? formatSumInsured(cent / 100) : null;

/**
 * "₹50 Lakh cover over ₹3 Lakh deductible · 1 year · Self, Spouse", the line under the plan
 * name everywhere.
 */
export const planSummaryLine = (
  cover: Parameters<typeof formatCover>[0] & {
    tenureYears: number;
    /** Paise; a super top-up names the deductible the cover sits over. */
    deductibleCent?: number | null;
  },
  members: { relation: string }[],
) => {
  const deductible = formatDeductibleCent(cover.deductibleCent);
  const coverLine = deductible
    ? `${formatCover(cover)} cover over ${deductible} deductible`
    : `${formatCover(cover)} cover`;
  return `${coverLine} · ${formatTenure(cover.tenureYears)} · ${memberNames(members)}`;
};

export const premiumFromSummary = (summary: IStuPaymentSummary): IStuPremium => {
  const { payableCent, basePremiumCent } = summary.price;

  return {
    totalCent: payableCent,
    tenureYears: summary.cover.tenureYears,
    lines: [
      ...(typeof basePremiumCent === 'number'
        ? [{ key: 'base', label: 'Base premium', amountCent: basePremiumCent, group: 'plan' as const }]
        : []),
      ...(typeof summary.price.taxCent === 'number'
        ? [{ key: 'tax', label: 'GST (18%)', amountCent: summary.price.taxCent, group: 'plan' as const }]
        : []),
      ...summary.addons.map((addon) => ({
        key: addon.coverCode,
        // "Borderless — 20 Percent", "Personal Accident — ₹10,00,000"
        label: [coverLabel(addon), formatAddonChoice(addon)]
          .filter(Boolean)
          .join(' — '),
        amountCent: addon.premiumCent,
        group: addon.isMandatory ? ('plan' as const) : ('addon' as const),
      })),
    ],
  };
};

export const premiumTermNote = (tenureYears: number) =>
  `For the full ${tenureYears}-year policy term.`;

/**
 * The plan-details premium, straight from the server: base + add-ons − discount = payable.
 * `addonsCent` covers the always-included covers as well as the picked add-ons, so both are
 * listed. Each gets its own line when the server priced every one of them; otherwise they show
 * as the one `addonsCent` line. Nothing is summed here.
 */
export const premiumFromSelection = (selection: IStuSelection): IStuPremium => {
  const { baseCent, addonsCent = null, discountCent, payableCent } = selection.premium;
  const billed = [
    ...selection.mandatoryCovers,
    ...selection.optionalCovers.filter((cover) => cover.selected),
  ];
  const hasSplit = [baseCent, addonsCent, discountCent].some((cent) => cent !== null);

  const addonLines: IStuPremiumLine[] = billed.every((cover) => cover.premiumCent !== null)
    ? billed.map((cover) => ({
        key: cover.coverCode,
        label: coverLabel(cover),
        amountCent: cover.premiumCent,
        group: 'addon',
      }))
    : [{ key: 'addons', label: 'Add-ons', amountCent: addonsCent, group: 'addon' }];

  return {
    totalCent: payableCent,
    tenureYears: selection.tenureYears,
    lines: hasSplit
      ? [
          ...(baseCent !== null
            ? [{ key: 'base', label: 'Base premium', amountCent: baseCent, group: 'plan' as const }]
            : []),
          ...(discountCent
            ? [{ key: 'discount', label: 'Discount', amountCent: -discountCent, group: 'plan' as const }]
            : []),
          ...(billed.length ? addonLines : []),
        ]
      : [],
  };
};
